import { useContext, useMemo } from 'react';

import { Tooltip } from '@mui/material';

import Body2 from 'components/Body2';
import Icon from 'components/Icon';
import IconButton from 'components/IconButton';
import IconWithLink from 'components/IconWithLink';
import Text from 'components/Text';
import CloseIcon from 'components/icons/CloseIcon';

import purlAgentContext from 'contexts/purlAgentContext';

import formatPhoneNumber from 'utilities/formatPhoneNumber';
import {
    PURL_AGENT_CODE,
    PURL_AGENT_CODE_NEW,
    PURL_AGENT_DATA
} from 'utilities/sessionStorageKeys';

import arrow from './images/arrow.svg';
import envelope from './images/envelope.svg';
import online from './images/online.svg';
import phone from './images/phone.svg';

import styles from './styles.module.scss';

import { remove } from 'temp/utilities/storage';

const AgentPurlBannerThin = () => {
    const { purlAgentData } = useContext(purlAgentContext);

    const { isPurlToggledOn } = purlAgentData || {};
    const agentAvailableComponent = useMemo(() => {
        if (isPurlToggledOn === true) {
            return (
                <span className={styles.onlineStyle}>
                    {' '}
                    <Icon className={`${styles.onlineIcon}`} image={online} />
                    <Body2
                        className={styles.availableText}
                        text="Available Now"
                    />
                    <Body2
                        className={styles.availableTextMobile}
                        text="Available"
                    />
                </span>
            );
        } else {
            return null;
        }
    }, [isPurlToggledOn]);

    const handleClose = (isReturnToAgent = false) => {
        remove(PURL_AGENT_CODE_NEW);
        remove(PURL_AGENT_CODE);
        remove(PURL_AGENT_DATA);
        localStorage.removeItem('reqAgentData');
        if (isReturnToAgent) {
            window.location.href = `${process.env.REACT_APP_APP_BASE_URL}/contact-an-agent`;
        } else {
            if (window.location.href.indexOf('/request-call-back') === -1) {
                window.location.href.indexOf('?purl=') &&
                    (window.location.href = window.location.href.replace(
                        `?purl=${purlAgentData.agentPurlCode}`,
                        ''
                    ));
            } else {
                window.location.href = `${process.env.REACT_APP_APP_BASE_URL}/request-call-back`;
            }
        }
    };

    if (purlAgentData) {
        const phoneNumber = purlAgentData.virtualNumber?.substring(2);
        return (
            <>
                <header
                    className={`${styles.purlBannerThin} ${styles.desktopBanner}`}
                >
                    <div className={styles.purlBannerThinContent}>
                        <div className={styles.purlAgentDetailsContainer}>
                            {purlAgentData.photo && (
                                <img
                                    className={styles.purlAgentImage}
                                    src={purlAgentData.photo}
                                    alt={`${purlAgentData.agentFirstName}`}
                                />
                            )}
                            <div className={styles.purlAgentNameContainer}>
                                <h2 className={styles.purlAgentName}>
                                    {purlAgentData.agentFirstName}{' '}
                                    {purlAgentData.agentLastName}
                                    {agentAvailableComponent}
                                </h2>
                                <p className={styles.purlAgentSubText}>
                                    Licensed Agent | NPN:{' '}
                                    {purlAgentData.agentNpn}
                                </p>
                                {localStorage.getItem('reqAgentData') && (
                                    <IconWithLink
                                        caption="Return to Map"
                                        className={styles.iconWithLink}
                                        iconClassName={styles.iconClassName}
                                        image={arrow}
                                        labelClassName={styles.labelClassName}
                                        onClick={() => handleClose(true)}
                                    />
                                )}
                            </div>
                        </div>
                        <div className={styles.purlAgentContactContainer}>
                            <a
                                className={`${styles.link} ${styles.emailLink}`}
                                href={`mailto:${purlAgentData.email}`}
                                rel="noreferrer"
                            >
                                {' '}
                                {purlAgentData.email}{' '}
                                <Icon
                                    className={`${styles.envelopeIcon}`}
                                    image={envelope}
                                />{' '}
                            </a>

                            {phoneNumber && (
                                <a
                                    className={styles.link}
                                    href={`tel:${phoneNumber}`}
                                    rel="noreferrer"
                                >
                                    {formatPhoneNumber(phoneNumber)}{' '}
                                    <Icon
                                        className={`${styles.phoneIcon}`}
                                        image={phone}
                                    />{' '}
                                </a>
                            )}
                        </div>
                    </div>
                    {localStorage.getItem('reqAgentData') && (
                        <Tooltip title="Remove Agent Experience">
                            <div className={styles.closeIconDiv}>
                                <CloseIcon
                                    className={styles.closeIconClassName}
                                    iconColor={'white'}
                                    onClick={() => handleClose()}
                                    tabIndex="0"
                                />
                            </div>
                        </Tooltip>
                    )}
                </header>
                <header
                    className={`${styles.purlBannerThin} ${styles.mobileBanner}`}
                    id="purlBannerThin"
                >
                    {localStorage.getItem('reqAgentData') && (
                        <Tooltip title="Remove Agent Experience">
                            <div className={styles.closeIconDiv}>
                                <CloseIcon
                                    className={styles.closeIconClassName}
                                    iconColor={'white'}
                                    onClick={() => handleClose()}
                                    tabIndex="0"
                                />
                            </div>
                        </Tooltip>
                    )}
                    <div className={styles.purlBannerThinContent}>
                        <div className={styles.purlAgentMobileDetails}>
                            {purlAgentData.photo && (
                                <img
                                    className={styles.purlAgentImage}
                                    src={purlAgentData.photo}
                                    alt={purlAgentData.agentFirstName}
                                />
                            )}
                            <div className={styles.purlAgentNameContainer}>
                                <h2 className={styles.purlAgentName}>
                                    {purlAgentData.agentFirstName}{' '}
                                    {purlAgentData.agentLastName}
                                    {agentAvailableComponent}
                                </h2>
                                <Text
                                    className={styles.purlAgentSubText}
                                    text={'Licensed Agent'}
                                />
                                <Text
                                    className={styles.purlAgentSubText}
                                    text={`NPN: ${purlAgentData.agentNpn}`}
                                />
                                {localStorage.getItem('reqAgentData') && (
                                    <IconWithLink
                                        caption="Return to Map"
                                        className={styles.iconWithLink}
                                        iconClassName={styles.iconClassName}
                                        image={arrow}
                                        labelClassName={styles.labelClassName}
                                        onClick={() => handleClose(true)}
                                    />
                                )}
                            </div>
                        </div>
                        <div className={styles.purlAgentContactContainer}>
                            <IconButton
                                className={`${styles.link} ${styles.emailLink}`}
                                iconClassName={styles.envelopeIcon}
                                icon={envelope}
                                onClick={() =>
                                    (window.location.href = `mailto:${purlAgentData.email}`)
                                }
                            />
                            <IconButton
                                className={`${styles.link} ${styles.emailLink}`}
                                iconClassName={styles.phoneIcon}
                                icon={phone}
                                onClick={() =>
                                    (window.location.href = `tel:${phoneNumber}`)
                                }
                            />
                        </div>
                    </div>
                </header>
            </>
        );
    }
    return null;
};

export default AgentPurlBannerThin;
